/**
 * Layout Component - Main application shell with sidebar navigation
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";

type Page = "dashboard" | "agents" | "channels" | "logs" | "settings";

@customElement("piagnet-layout")
export class Layout extends LitElement {
	@state() private currentPage: Page = "dashboard";
	@state() private sidebarOpen = true;

	static styles = css`
		:host {
			display: flex;
			height: 100vh;
			font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
		}

		sidebar {
			width: 250px;
			background: #111827;
			color: white;
			display: flex;
			flex-direction: column;
			transition: width 0.3s;
		}

		sidebar.collapsed {
			width: 64px;
		}

		.logo {
			display: flex;
			align-items: center;
			gap: 0.75rem;
			padding: 1rem 1.25rem;
			border-bottom: 1px solid #374151;
		}

		.logo-icon {
			width: 32px;
			height: 32px;
			background: linear-gradient(135deg, #3b82f6, #8b5cf6);
			border-radius: 8px;
			display: flex;
			align-items: center;
			justify-content: center;
			font-weight: bold;
			font-size: 1rem;
		}

		.logo-text {
			font-size: 1.25rem;
			font-weight: 600;
		}

		sidebar.collapsed .logo-text {
			display: none;
		}

		.nav {
			flex: 1;
			padding: 1rem 0;
		}

		.nav-item {
			display: flex;
			align-items: center;
			gap: 0.75rem;
			padding: 0.75rem 1.25rem;
			color: #9ca3af;
			cursor: pointer;
			transition: all 0.2s;
			border: none;
			background: none;
			width: 100%;
			text-align: left;
			font-size: 0.875rem;
		}

		.nav-item:hover {
			background: #1f2937;
			color: white;
		}

		.nav-item.active {
			background: #1f2937;
			color: #3b82f6;
			border-right: 3px solid #3b82f6;
		}

		.nav-icon {
			width: 20px;
			text-align: center;
		}

		sidebar.collapsed .nav-text {
			display: none;
		}

		.main {
			flex: 1;
			display: flex;
			flex-direction: column;
			background: #f3f4f6;
			overflow: hidden;
		}

		.header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 0.75rem 1.5rem;
			background: white;
			border-bottom: 1px solid #e5e7eb;
		}

		.toggle-btn {
			background: none;
			border: none;
			cursor: pointer;
			padding: 0.5rem;
			color: #6b7280;
			font-size: 1.25rem;
		}

		.content {
			flex: 1;
			overflow: auto;
		}
	`;

	render() {
		return html`
			<sidebar class="${this.sidebarOpen ? "" : "collapsed"}">
				<div class="logo">
					<div class="logo-icon">π</div>
					<span class="logo-text">Piagnet</span>
				</div>
				<nav class="nav">
					<button class="nav-item ${this.currentPage === "dashboard" ? "active" : ""}" @click="${() => this.navigate("dashboard")}">
						<span class="nav-icon">📊</span>
						<span class="nav-text">Dashboard</span>
					</button>
					<button class="nav-item ${this.currentPage === "agents" ? "active" : ""}" @click="${() => this.navigate("agents")}">
						<span class="nav-icon">🤖</span>
						<span class="nav-text">Agents</span>
					</button>
					<button class="nav-item ${this.currentPage === "channels" ? "active" : ""}" @click="${() => this.navigate("channels")}">
						<span class="nav-icon">📡</span>
						<span class="nav-text">Channels</span>
					</button>
					<button class="nav-item ${this.currentPage === "logs" ? "active" : ""}" @click="${() => this.navigate("logs")}">
						<span class="nav-icon">📜</span>
						<span class="nav-text">Logs</span>
					</button>
					<button class="nav-item ${this.currentPage === "settings" ? "active" : ""}" @click="${() => this.navigate("settings")}">
						<span class="nav-icon">⚙️</span>
						<span class="nav-text">Settings</span>
					</button>
				</nav>
			</sidebar>

			<main class="main">
				<header class="header">
					<button class="toggle-btn" @click="${() => this.sidebarOpen = !this.sidebarOpen}">
						${this.sidebarOpen ? "◀" : "▶"}
					</button>
					<div>Piagnet Admin</div>
					<div></div>
				</header>
				<div class="content">
					${this.renderContent()}
				</div>
			</main>
		`;
	}

	private renderContent() {
		switch (this.currentPage) {
			case "dashboard":
				return html`<piagnet-dashboard></piagnet-dashboard>`;
			case "agents":
				return html`<piagnet-agent-config></piagnet-agent-config>`;
			default:
				return html`<div style="padding: 2rem; text-align: center; color: #6b7280;">
					<h2>Coming Soon</h2>
					<p>This page is under construction</p>
				</div>`;
		}
	}

	private navigate(page: Page) {
		this.currentPage = page;
	}
}
